import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Reusable SEO meta tags component.
 * Drop <Seo title="..." description="..." /> at the top of any page.
 */
export default function Seo({
  title,
  description,
  image,
  type = "website",
  noindex = false,
  jsonLd,
  keywords,
}) {
  const fullTitle = title
    ? `${title} · TTI Holidays`
    : "Tour Trips India Holidays — India · Nepal · Dubai · Thailand";
  // og:title is the page-specific title WITHOUT brand suffix — looks cleaner in social previews
  const ogTitle = title || "Tour Trips India Holidays — Crafted Journeys Through Incredible India";
  const desc =
    description ||
    "Hand-crafted journeys through Incredible India — Golden Triangle, Spiritual India, Rajasthan, Wildlife, Festivals and seamless extensions to Nepal, Dubai and Thailand. Family-run from Agra.";
  const img =
    image ||
    "https://customer-assets.emergentagent.com/job_india-tour-trips/artifacts/bszk0n38_TTI%20NEW%20PNG.png";
  // Canonical URL: strip query string and hash for clean indexing
  const url =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}`
      : "";

  // jsonLd may be a single object or an array of objects — normalise to array
  const ldArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {Array.isArray(keywords) && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(", ")} />
      )}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={url} />

      {/* Open Graph (Facebook, LinkedIn, WhatsApp) */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={img} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="Tour Trips India Holidays" />
      <meta property="og:locale" content="en_US" />
      {/* Alternate locales for Latin American + European markets */}
      <meta property="og:locale:alternate" content="es_ES" />
      <meta property="og:locale:alternate" content="es_MX" />
      <meta property="og:locale:alternate" content="pt_BR" />
      <meta property="og:locale:alternate" content="pt_PT" />
      <meta property="og:locale:alternate" content="fr_FR" />
      <meta property="og:locale:alternate" content="de_DE" />
      <meta property="og:locale:alternate" content="it_IT" />
      <meta property="og:locale:alternate" content="ru_RU" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {/* JSON-LD structured data — must be native <script> children of <Helmet> */}
      {ldArray.map((data, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(data)}</script>
      ))}
    </Helmet>
  );
}
